import type { CompanyWatermarkSettings } from './types';

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { HTTPError } from '~/services/kyClient';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyWatermarkSettings } from './fetchCompanyWatermarkSettings';

/**
 * React Query based custom hook for getting the watermark settings for a company with a given companyId.
 * @param companyId - The ID of the company to fetch watermark settings for.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_watermark_settings_vadmin_company__company_id__watermark_settings_get
 */
export const useQueryCompanyWatermarkSettings = (
  companyId: UUID | undefined,
  options: Omit<
    UseQueryOptions<CompanyWatermarkSettings>,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery<CompanyWatermarkSettings>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompanyWatermarkSettings(companyId!),
    queryKey: queryKeysCompany.getWatermarkSettings(companyId!),
    retry(failureCount, error) {
      // Don't retry on 404 errors - the resource doesn't exist
      if (error instanceof HTTPError && error.response.status === 404) {
        return false;
      }

      // Use default retry logic for other errors (3 retries)
      return failureCount < 3;
    },
    staleTime: ms('60s'),
    ...options,
  });
};
